import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Formik } from "formik";
import { Slide, ToastContainer, toast } from "react-toastify";
import { DefaultLayout } from "../../../../layouts/Default";
import { Main } from "../../../../themes/Default/components/Main";
import { Title } from "../../../../themes/Default/components/Title";
import { TopNavigation } from "../../../../themes/Default/components/TopNavigation";
import { Breadcrumps } from "../../../../themes/Default/components/Breadcrumps";
import { Link } from "../../../../themes/Default/components/Breadcrumps/components/Link";
import { Button, ButtonBack } from "../../../../themes/Default/components/TopNavigation/components/Button";
import { List } from "../../../../themes/Default/components/List";
import { Header as ListHeader } from "../../../../themes/Default/components/List/components/Header";
import { Body as ListBody } from "../../../../themes/Default/components/List/components/Body";
import { Pagination } from "../../../../themes/Default/components/List/components/Footer/components/Pagination";
import { TableHead } from "../../../../themes/Default/components/List/components/Body/components/TableHead";
import { TableBody } from "../../../../themes/Default/components/List/components/Body/components/TableBody";
import { TableRow } from "../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow";
import { TableHeadCell } from "../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow/components/TableHeadCell";
import { TableDataCell } from "../../../../themes/Default/components/List/components/Body/components/TableBody/components/TableDataCell";
import { Title as ListTitle } from "../../../../themes/Default/components/List/components/Header/components/Title";
import { Menu as ListMenu } from "../../../../themes/Default/components/List/components/Header/components/Menu";
import { Filter as ListFilter } from "../../../../themes/Default/components/List/components/Header/components/Filter";
import { Button as ListMenuButton } from "../../../../themes/Default/components/List/components/Header/components/Menu/components/Button";
import { Checkbox } from "../../../../themes/Default/components/Form/components/Checkbox";
import { Form } from "../../../../themes/Default/components/Form";
import { Row } from "../../../../themes/Default/components/Form/components/Row";
import { Field } from "../../../../themes/Default/components/Form/components/Row/components/Field";
import { Input } from "../../../../themes/Default/components/Form/components/Input";
import { locationsManagement } from "../../../../hooks/api/LocationsManagement";
import { getNumberOfUsedFilters } from "../../../../services/utils/Filter";
import { useTranslation } from "react-i18next";
import { Tabs } from "../../../../themes/Default/components/Tabs";
import { Toaster } from "../../../../themes/Default/components/Toaster";

const Locations = () => {
    const { t } = useTranslation("common");
    const recordsPerPage = 10;
    const defaultFilters = {
        startPage: 0,
        limitPage: recordsPerPage,
    };
    const formRef = useRef(null);
    const navigate = useNavigate();
    const [filters, setFilters] = useState(defaultFilters);
    const [currentPage, setCurrentPage] = useState(1);
    const [showFilter, setShowFilter] = useState(false);
    const [disableDelete, setDisableDelete] = useState(true);
    const [selectedLocations, setSelectedLocations] = useState([]);
    const [numberOfUserdFilters, setNumberOfUserdFilters] = useState();
    const [totalRecords, setTotalRecords] = useState(0);
    const [locations, setLocations] = useState();
    const { useLocationListMutation, useDeleteLocationMutation } = locationsManagement;
    const [locationList, { isLoading }] = useLocationListMutation();
    const [deleteLocation] = useDeleteLocationMutation();

    const tabs = [
        { title: t("locations.title"), isActive: true },
        { title: t("legalEntity.title"), onClick: () => gotoLegalEntityTab() },
    ];

    const gotoLegalEntityTab = () => {
        navigate("/settings/locations/legal-entities");
    }

    const openFilter = () => {
        setShowFilter(true);
    };

    const closeFilter = () => {
        setShowFilter(false);
    };

    const gotoNew = () => {
        navigate("/settings/locations/locations/add");
    };

    const gotoEdit = (locationId) => {
        navigate("/settings/locations/locations/edit/" + locationId);
    };

    const onFilter = () => {
        if (formRef.current) {
            formRef.current.handleSubmit();
        }
    };

    const onFiltered = (values) => {
        setFilters({
            ...filters,
            startPage: 0,
            name: values.name,
            address: values.address,
        });
    };

    const onResetFilter = () => {
        setFilters(defaultFilters);
        setShowFilter(false);
        if (formRef.current) {
            formRef.current.resetForm();
        }
    };

    const onPageChange = (currentPage) => {
        setCurrentPage(currentPage);
        const newStartPage = (currentPage - 1) * recordsPerPage;
        setFilters({
            ...filters,
            startPage: newStartPage,
            limitPage: recordsPerPage,
        });
    };

    const onSelectionChange = (id) => {
        setSelectedLocations((prevSelected) => {
            if (prevSelected.includes(id)) {
                return prevSelected.filter((recordId) => recordId !== id);
            } else {
                return [...prevSelected, id];
            }
        });
    };

    const onDelete = () => {
        toast.warn(
            ({ closeToast }) => (
                <Toaster
                    closeToast={closeToast}
                    description={t("areYouSure")}
                    optionNo={t("no")}
                    optionYes={t("yes")}
                    onClick={() => {
                        closeToast();
                        for (let i = 0; i < selectedLocations?.length; i++) {
                            const locationId = selectedLocations[i];
                            deleteLocation(locationId).then((result) => {
                                if (result?.error) {
                                    toast.error(result.error.data.error.responseMessage);
                                } else {
                                    locationList(filters).then((result) => {
                                        const locations = result?.data?.records;
                                        setLocations(locations);
                                        setTotalRecords(result?.data?.totalRecords);
                                    });
                                    setDisableDelete(true);
                                    toast.info(result?.data?.info?.responseMessage);
                                    onSelectionChange(locationId);
                                }
                            });
                        }
                    }}
                />
            ),
            {
                autoClose: false,
                progress: false,
                closeButton: false,
            },
        );
    };

    useEffect(() => {
        if (selectedLocations.length > 0) {
            setDisableDelete(false);
        } else {
            setDisableDelete(true);
        }
    }, [selectedLocations]);

    useEffect(() => {
        locationList(filters).then((result) => {
            const locations = result?.data?.records;
            setLocations(locations);
            setTotalRecords(result?.data?.totalRecords);
        });
        setNumberOfUserdFilters(getNumberOfUsedFilters(filters));
    }, [filters, locationList]);

    return (
        <DefaultLayout>
            
            <TopNavigation>
                <ButtonBack to="/settings" />
                <Button title={t("locations.addLocation")} icon="fa-regular fa-plus" onClick={gotoNew}></Button>
                <Button title={t("delete")} icon="fa-regular fa-trash-can" isDisabled={disableDelete} onClick={onDelete}></Button>
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Title>{`${t("locations.title")} (${totalRecords})`}</Title>
                <Breadcrumps>
                    <Link title={t("settings.title")} icon="fa-gear" />
                    <Link title={t("locations.title")} />
                    <Link title={t("locations.title")} />
                    <Link title={t("settings.list")} isActive={true} />
                </Breadcrumps>
                {!isLoading && (
                    <>
                        <List>
                            <ListHeader>
                                <ListMenu>
                                    <ListMenuButton title="Filter" icon="fa-filter" onClick={openFilter} badge={numberOfUserdFilters} />
                                </ListMenu>
                                <ListFilter isVisible={showFilter} onClose={closeFilter} onFilter={onFilter} onResetFilter={onResetFilter}>
                                    <Formik innerRef={formRef} initialValues={{ name: filters?.name, address: filters?.address }} onSubmit={onFiltered}>
                                        {({ values, handleChange, handleSubmit, resetForm }) => (
                                            <Form>
                                                <Row>
                                                    <Field>
                                                        <Input name="name" label={t("locations.name")} value={values.name} onChange={handleChange} />
                                                    </Field>
                                                </Row>
                                                <Row>
                                                    <Field>
                                                        <Input name="address" label={t("locations.address")} value={values.address} onChange={handleChange} />
                                                    </Field>
                                                </Row>
                                            </Form>
                                        )}
                                    </Formik>
                                </ListFilter>
                            </ListHeader>
                            <ListBody>
                                <TableHead>
                                    <TableRow>
                                        <TableHeadCell></TableHeadCell>
                                        <TableHeadCell></TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("locations.name")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("locations.address")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("locations.contactPerson")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("locations.phoneNumber")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell></TableHeadCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {locations?.length > 0 &&
                                        locations?.map((location, index) => {
                                            return (
                                                <TableRow key={location.id}>
                                                    <TableDataCell>
                                                        <Button icon="fa-regular fa-pen" onClick={() => gotoEdit(location.id)}></Button>
                                                    </TableDataCell>
                                                    <TableDataCell>
                                                        <Checkbox name="isBlockClass" value={location.id} onChange={() => onSelectionChange(location.id)} />
                                                    </TableDataCell>
                                                    <TableDataCell>{location.name}</TableDataCell>
                                                    <TableDataCell>{location.address}</TableDataCell>
                                                    <TableDataCell>{location.contactPersonFullName}</TableDataCell>
                                                    <TableDataCell>{location.phoneNumber}</TableDataCell>
                                                    <TableDataCell></TableDataCell>
                                                </TableRow>
                                            );
                                        })}
                                </TableBody>
                            </ListBody>
                        </List>
                        <Pagination currentPage={currentPage} recordsPerPage={recordsPerPage} totalRecords={totalRecords} onClick={onPageChange} />
                    </>
                )}
            </Main>
        </DefaultLayout>
    );
};

export default Locations;
